import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  IconButton, 
  useTheme,
  Box,
  Fade,
  Zoom,
  styled,
  Collapse,
  Paper,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  LinearProgress
} from '@mui/material';
import { getFirestore, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, auth } from '../Firebase/Config';
import { useNavigate } from 'react-router-dom';

import Person from '@mui/icons-material/Person';
import { 
  MailOutline, 
  Facebook, 
  Twitter, 
  Instagram,
  LinkedIn,
  PointOfSale,
  CreditCard,
  Receipt,
  Devices,
  Assessment,
  Restaurant,
  Timer,
  RocketLaunch,
  Stars,
  NotificationsActive,
  CheckCircleOutline,
  TrendingUp,
  Security,
  Speed,
  Login,
  Save,
  ArrowBack,
  ArrowForward,
  Print,
  QrCodeScanner,
  LocalPrintshop,
  Storefront,
  Settings,
  Description,
  Phone,
  LocationOn,
  Business,
  Store
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';

const floating = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const scaleUp = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(37, 99, 235, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.1,
    pointerEvents: 'none',
  }
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '32px',
  padding: theme.spacing(4),
  boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.06)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 30px 50px rgba(0,0,0,0.15)',
  }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
  borderRadius: '24px',
  padding: theme.spacing(3),
  textAlign: 'center',
  transition: 'all 0.4s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .feature-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 3s infinite linear`,
  }
}));

const FeatureIcon = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  margin: '0 auto 20px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontSize: '2.5rem',
  transition: 'all 0.3s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '2.5rem',
  }
}));

const CountdownBox = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
  borderRadius: '24px',
  padding: theme.spacing(3),
  textAlign: 'center',
  minWidth: 120,
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
  }
}));

const CountdownNumber = styled(Typography)(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  animation: `${floating} 3s ease-in-out infinite`,
}));

const FloatingIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  animation: `${floating} 6s ease-in-out infinite`,
  opacity: 0.1,
  zIndex: 0,
  pointerEvents: 'none',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  }
}));

const steps = ['Informations Personnelles', 'Configuration Souhaitée', 'Besoins Spécifiques'];

const IntegrationCaisseComingSoon = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // Form Data State - Only required fields
  const [formData, setFormData] = useState({
    // Step 1: Informations Personnelles
    fullName: '',
    email: '',
    phone: '',
    restaurantName: '',
    restaurantType: '',
    city: '',
    
    // Step 2: Configuration Souhaitée
    registerType: 'classic',
    compatibleSystems: [],
    printerType: 'thermal',
    barcodeScanner: true,
    touchScreen: true,
    offlineMode: true,
    
    // Step 3: Besoins Spécifiques
    hasExistingCaisse: false,
    existingCaisseProvider: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  
  const LAUNCH_DATE = new Date('2025-07-01T00:00:00');
  
  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setFormData(prev => ({
          ...prev,
          email: currentUser.email || '',
          fullName: currentUser.displayName || ''
        }));
        setCheckingAuth(false);
      } else {
        setUser(null);
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Date.now();
      const difference = LAUNCH_DATE - now;
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
        return true;
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return false;
      }
    };
    
    const timerActive = calculateTimeLeft();
    let timer;
    if (timerActive) {
      timer = setInterval(() => {
        if (!calculateTimeLeft()) {
          clearInterval(timer);
        }
      }, 1000);
    }
    
    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Le nom complet est requis';
      if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
      if (!formData.restaurantName.trim()) newErrors.restaurantName = 'Le nom du restaurant est requis';
      if (!formData.restaurantType) newErrors.restaurantType = 'Le type de restaurant est requis';
    }
    
    if (activeStep === 1) {
      if (!formData.registerType) newErrors.registerType = 'Le type de caisse est requis';
      if (formData.compatibleSystems.length === 0) newErrors.compatibleSystems = 'Sélectionnez au moins un système compatible';
    }
    
    if (activeStep === 2) {
      if (formData.hasExistingCaisse && !formData.existingCaisseProvider.trim()) {
        newErrors.existingCaisseProvider = 'Veuillez indiquer le fournisseur de votre caisse actuelle';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!user) {
      setSnackbar({ 
        open: true, 
        message: 'Veuillez vous connecter pour envoyer votre demande', 
        severity: 'warning' 
      });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (validateStep()) {
      setLoading(true);
      try {
        const requestData = {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          restaurantName: formData.restaurantName,
          restaurantType: formData.restaurantType,
          city: formData.city,
          registerType: formData.registerType,
          compatibleSystems: formData.compatibleSystems,
          printerType: formData.printerType,
          barcodeScanner: formData.barcodeScanner,
          touchScreen: formData.touchScreen,
          offlineMode: formData.offlineMode,
          hasExistingCaisse: formData.hasExistingCaisse,
          existingCaisseProvider: formData.hasExistingCaisse ? formData.existingCaisseProvider : null,
          message: formData.message,
          userId: user.uid,
          userEmail: user.email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'pending',
          source: 'coming_soon_form'
        };

        await addDoc(collection(db, 'integrationRequests'), requestData);
        
        setSubmitted(true);
        setSnackbar({ 
          open: true, 
          message: 'Votre demande a été envoyée avec succès! Nous vous contacterons bientôt.', 
          severity: 'success' 
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            fullName: '',
            email: user?.email || '',
            phone: '',
            restaurantName: '',
            restaurantType: '',
            city: '',
            registerType: 'classic',
            compatibleSystems: [],
            printerType: 'thermal',
            barcodeScanner: true,
            touchScreen: true,
            offlineMode: true,
            hasExistingCaisse: false,
            existingCaisseProvider: '',
            message: '',
          });
          setActiveStep(0);
        }, 3000);
        
      } catch (error) {
        console.error('Error saving request:', error);
        let errorMessage = 'Erreur lors de l\'envoi de la demande';
        if (error.code === 'permission-denied') {
          errorMessage = 'Permission refusée. Veuillez réessayer.';
        }
        setSnackbar({ 
          open: true, 
          message: errorMessage, 
          severity: 'error' 
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom Complet *"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                error={!!errors.fullName}
                helperText={errors.fullName}
                variant="outlined"
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: '#667eea' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
                disabled={!!user}
                InputProps={{
                  startAdornment: <MailOutline sx={{ mr: 1, color: '#667eea' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: '#667eea' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du Restaurant *"
                value={formData.restaurantName}
                onChange={(e) => handleInputChange('restaurantName', e.target.value)}
                error={!!errors.restaurantName}
                helperText={errors.restaurantName}
                variant="outlined"
                InputProps={{
                  startAdornment: <Restaurant sx={{ mr: 1, color: '#667eea' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.restaurantType}>
                <InputLabel>Type de Restaurant *</InputLabel>
                <Select
                  value={formData.restaurantType}
                  onChange={(e) => handleInputChange('restaurantType', e.target.value)}
                  label="Type de Restaurant *"
                >
                  <MenuItem value="restaurant">Restaurant Gastronomique</MenuItem>
                  <MenuItem value="brasserie">Brasserie</MenuItem>
                  <MenuItem value="fastfood">Fast Food</MenuItem>
                  <MenuItem value="cafe">Café / Salon de Thé</MenuItem>
                  <MenuItem value="foodtruck">Food Truck</MenuItem>
                  <MenuItem value="hotel">Restaurant d'Hôtel</MenuItem>
                </Select>
                {errors.restaurantType && <FormHelperText>{errors.restaurantType}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#667eea' }} />,
                }}
              />
            </Grid>
          </Grid>
        );
      
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.registerType}>
                <InputLabel>Type de Caisse Souhaité *</InputLabel>
                <Select
                  value={formData.registerType}
                  onChange={(e) => handleInputChange('registerType', e.target.value)}
                  label="Type de Caisse Souhaité *"
                >
                  <MenuItem value="classic">Caisse Classique</MenuItem>
                  <MenuItem value="touch">Caisse Tactile</MenuItem>
                  <MenuItem value="mobile">Caisse Mobile</MenuItem>
                  <MenuItem value="tablet">Tablette Tactile</MenuItem>
                  <MenuItem value="allinone">Solution Tout-en-Un</MenuItem>
                </Select>
                {errors.registerType && <FormHelperText>{errors.registerType}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.compatibleSystems}>
                <InputLabel>Systèmes d'Exploitation *</InputLabel>
                <Select
                  multiple
                  value={formData.compatibleSystems}
                  onChange={(e) => handleInputChange('compatibleSystems', e.target.value)}
                  label="Systèmes d'Exploitation *"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Windows">Windows</MenuItem>
                  <MenuItem value="MacOS">MacOS</MenuItem>
                  <MenuItem value="Linux">Linux</MenuItem>
                  <MenuItem value="iOS">iOS</MenuItem>
                  <MenuItem value="Android">Android</MenuItem>
                </Select>
                {errors.compatibleSystems && <FormHelperText>{errors.compatibleSystems}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type d'Imprimante</InputLabel>
                <Select
                  value={formData.printerType}
                  onChange={(e) => handleInputChange('printerType', e.target.value)}
                  label="Type d'Imprimante"
                >
                  <MenuItem value="thermal">Thermique</MenuItem>
                  <MenuItem value="dotmatrix">Matricielle</MenuItem>
                  <MenuItem value="laser">Laser</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#1a1a2e', mb: 2, fontWeight: 600 }}>
                Équipements Souhaités
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.barcodeScanner}
                      onChange={(e) => handleInputChange('barcodeScanner', e.target.checked)}
                      sx={{ color: '#667eea' }}
                    />
                  }
                  label="Lecteur de code-barres"
                  sx={{ color: '#1a1a2e' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.touchScreen}
                      onChange={(e) => handleInputChange('touchScreen', e.target.checked)}
                      sx={{ color: '#667eea' }}
                    />
                  }
                  label="Écran tactile"
                  sx={{ color: '#1a1a2e' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.offlineMode}
                      onChange={(e) => handleInputChange('offlineMode', e.target.checked)}
                      sx={{ color: '#667eea' }}
                    />
                  }
                  label="Mode hors ligne"
                  sx={{ color: '#1a1a2e' }}
                />
              </FormGroup>
            </Grid>
          </Grid>
        );
      
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#1a1a2e', mb: 2, fontWeight: 600 }}>
                Informations sur votre caisse actuelle
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasExistingCaisse}
                      onChange={(e) => handleInputChange('hasExistingCaisse', e.target.checked)}
                      sx={{ color: '#667eea' }}
                    />
                  }
                  label="Je possède déjà une caisse"
                  sx={{ color: '#1a1a2e' }}
                />
              </FormGroup>
            </Grid>
            
            {formData.hasExistingCaisse && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fournisseur de la caisse actuelle *"
                  value={formData.existingCaisseProvider}
                  onChange={(e) => handleInputChange('existingCaisseProvider', e.target.value)}
                  error={!!errors.existingCaisseProvider}
                  helperText={errors.existingCaisseProvider}
                  placeholder="Ex: POSlik, Square, Toast, etc."
                  variant="outlined"
                  InputProps={{
                    startAdornment: <Store sx={{ mr: 1, color: '#667eea' }} />,
                  }}
                />
                <FormHelperText>Indiquez le nom du fournisseur de votre système de caisse actuel</FormHelperText>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message / Besoins Spécifiques"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                placeholder="Décrivez vos besoins spécifiques, vos attentes, ou toute information complémentaire..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
                <strong>Récapitulatif :</strong>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">• Restaurant: {formData.restaurantName}</Typography>
                  <Typography variant="body2">• Type de caisse souhaité: {formData.registerType}</Typography>
                  {formData.hasExistingCaisse && (
                    <Typography variant="body2">• Caisse actuelle: {formData.existingCaisseProvider}</Typography>
                  )}
                </Box>
              </Alert>
            </Grid>
          </Grid>
        );
      
      default:
        return null;
    }
  };

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
        <Typography sx={{ color: 'white' }}>
          Chargement...
        </Typography>
      </Box>
    );
  }

  return (
    <StyledContainer maxWidth="lg">
      {/* Floating Icons */}
      {floatingIcons.map((item, index) => (
        <FloatingIcon
          key={index}
          sx={{
            top: item.top,
            left: item.left,
            right: item.right,
            bottom: item.bottom,
            animation: `${floating} ${6 + index}s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
          }}
        >
          {React.cloneElement(item.icon, { sx: { fontSize: 80, color: 'white' } })}
        </FloatingIcon>
      ))}

      <Box position="relative" zIndex={1}>
        {/* Header */}
        <Fade in timeout={1000}>
          <Box textAlign="center" mb={4}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Intégration Caisse
            </Typography>
            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Préparez votre restaurant pour la révolution digitale
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                Connecté en tant que: {user.email}
              </Typography>
            )}
          </Box>
        </Fade>

        {/* Countdown Timer */}
      
        {/* Features Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in timeout={(index + 1) * 200}>
                <FeatureCard elevation={0}>
                  <FeatureIcon className="feature-icon" sx={{ bgcolor: feature.color }}>
                    {feature.icon}
                  </FeatureIcon>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Form Section */}
        <GlassCard>
          {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1a1a2e', textAlign: 'center', mb: 3 }}>
            Demande d'Intégration
          </Typography>
          
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <form>
            {renderStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBack />}
                sx={{ borderRadius: '50px', px: 3 }}
              >
                Précédent
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <StyledButton
                  onClick={handleSubmit}
                  disabled={loading || !user}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer ma Demande'}
                </StyledButton>
              ) : (
                <StyledButton
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                >
                  Suivant
                </StyledButton>
              )}
            </Box>
          </form>
          
          {!user && (
            <Alert severity="warning" sx={{ mt: 3, borderRadius: '12px' }}>
              Veuillez vous connecter pour envoyer votre demande d'intégration
            </Alert>
          )}
        </GlassCard>

        {/* Social & Footer */}
        <Box textAlign="center" mt={4}>
          <Divider sx={{ mb: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Typography variant="body1" gutterBottom sx={{ mb: 3, color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
            Suivez l'aventure en temps réel
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {[
              { icon: <Facebook />, color: '#1877f2', label: 'Facebook' },
              { icon: <Twitter />, color: '#1da1f2', label: 'Twitter' },
              { icon: <Instagram />, color: '#e4405f', label: 'Instagram' },
              { icon: <LinkedIn />, color: '#0077b5', label: 'LinkedIn' }
            ].map((social, index) => (
              <Tooltip title={social.label} key={index}>
                <IconButton
                  href="#"
                  target="_blank"
                  sx={{
                    background: 'rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: social.color,
                      transform: 'translateY(-5px) rotate(5deg)',
                    }
                  }}
                >
                  {React.cloneElement(social.icon, { sx: { fontSize: 28, color: 'white' } })}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          
          <Typography variant="caption" sx={{ mt: 4, display: 'block', color: 'rgba(255,255,255,0.6)' }}>
            © 2024 ERP Restaurant Ecosystem. Tous droits réservés.
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

const features = [
  { icon: <PointOfSale />, title: 'Intégration POS', description: 'Traitement sécurisé des paiements en temps réel', color: '#667eea' },
  { icon: <CreditCard />, title: 'Multi-paiements', description: 'Carte bancaire, mobile, espèces, tickets resto', color: '#764ba2' },
  { icon: <Receipt />, title: 'Facturation Éco', description: 'Reçus numériques et archivage cloud', color: '#f093fb' },
  { icon: <Devices />, title: 'Multi-appareils', description: 'Compatible avec tous vos équipements', color: '#4facfe' },
  { icon: <Assessment />, title: 'Analytics Pro', description: 'Tableaux de bord et rapports intelligents', color: '#00f2fe' },
  { icon: <Security />, title: 'Sécurité Max', description: 'Données cryptées et conformes RGPD', color: '#43e97b' },
];

const floatingIcons = [
  { icon: <Restaurant />, top: '10%', left: '5%', delay: 0 },
  { icon: <PointOfSale />, top: '85%', left: '10%', delay: 2 },
  { icon: <CreditCard />, top: '15%', right: '8%', delay: 1 },
  { icon: <Receipt />, bottom: '20%', right: '5%', delay: 3 },
  { icon: <TrendingUp />, top: '50%', left: '2%', delay: 2.5 },
  { icon: <Speed />, bottom: '10%', right: '15%', delay: 1.5 },
];

export default IntegrationCaisseComingSoon;